import { useNavigate, useSearchParams } from "react-router-dom";
import { HubAdminDemoLayout } from "@/layout/HubAdminDemoLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, Users, CheckSquare, DollarSign, ArrowRight } from "lucide-react";
import { INITIAL_REGISTRATIONS, type AgeGroup } from "./HubTournamentDemo";
import { MOCK_CANDIDATES } from "@/components/hub/mockSelectionData";

const AGE_GROUPS = ["All", "U11", "U13", "U15", "U17"] as const;
type AgeFilter = typeof AGE_GROUPS[number];

const RECENT_ACTIVITY = [
  { time: "2h ago",    text: "Lighthouse CC completed team registration for Spring Tournament" },
  { time: "4h ago",   text: "Payment received: North Star Cricket Academy — $150 deposit" },
  { time: "Yesterday", text: "Selection panel opened for U15 Spring Trial (3 selectors assigned)" },
  { time: "2d ago",   text: "Riverdale CC submitted roster update — 2 players swapped" },
  { time: "3d ago",   text: "Spring Tournament registration deadline extended to Apr 10" },
];

export default function HubDashboardDemo() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawAge = searchParams.get("ageGroup") ?? "All";
  const selectedAge: AgeFilter = (AGE_GROUPS as readonly string[]).includes(rawAge)
    ? (rawAge as AgeFilter)
    : "All";

  function selectAge(ag: AgeFilter) {
    if (ag === "All") {
      setSearchParams({});
    } else {
      setSearchParams({ ageGroup: ag });
    }
  }

  // Derive dynamic stat values from mock data
  const filteredRegs = selectedAge === "All"
    ? INITIAL_REGISTRATIONS
    : INITIAL_REGISTRATIONS.filter((r) => r.ageGroup === (selectedAge as AgeGroup));

  const filteredCandidates = selectedAge === "All"
    ? MOCK_CANDIDATES
    : MOCK_CANDIDATES.filter((c) => c.ageGroup === selectedAge);

  const teamsCount = filteredRegs.length;
  const pendingAmount = filteredRegs.reduce((sum, r) => sum + (r.amountDue - r.paid), 0);
  const hasTournament = teamsCount > 0;
  const hasSelection = filteredCandidates.length > 0;

  const ageQS = selectedAge === "All" ? "" : `&ageGroup=${selectedAge}`;

  const STAT_CARDS = [
    {
      label: "Active Tournaments",
      value: hasTournament ? "1" : "0",
      icon: Trophy,
      color: "text-blue-600",
      bg: "bg-blue-50",
      href: `/hub/tournament-demo${ageQS ? `?${ageQS.slice(1)}` : ""}`,
    },
    {
      label: "Teams Registered",
      value: String(teamsCount),
      icon: Users,
      color: "text-green-600",
      bg: "bg-green-50",
      href: `/hub/tournament-demo?tab=registration${ageQS}`,
    },
    {
      label: "Selection Cycles",
      value: hasSelection ? "1 active" : "0 active",
      icon: CheckSquare,
      color: "text-purple-600",
      bg: "bg-purple-50",
      href: `/hub/selection-demo${ageQS ? `?${ageQS.slice(1)}` : ""}`,
    },
    {
      label: "Pending Payments",
      value: `$${pendingAmount}`,
      icon: DollarSign,
      color: "text-amber-600",
      bg: "bg-amber-50",
      href: `/hub/tournament-demo?tab=payments${ageQS}`,
    },
  ];

  const tournamentHref = `/hub/tournament-demo${ageQS ? `?${ageQS.slice(1)}` : ""}`;
  const selectionHref  = `/hub/selection-demo${ageQS ? `?${ageQS.slice(1)}` : ""}`;

  return (
    <HubAdminDemoLayout>
      <div className="px-6 py-7 max-w-5xl mx-auto w-full space-y-8" data-testid="hub-dashboard-demo-page">
        {/* Page heading */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hub Admin Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Overview of your hub's active tournaments, selection cycles, and payments.</p>
        </div>

        {/* Age group filter pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-gray-500 mr-1">Age group:</span>
          {AGE_GROUPS.map((ag) => (
            <button
              key={ag}
              onClick={() => selectAge(ag)}
              className={`px-3 py-1 rounded-full text-sm font-semibold border transition-all ${
                selectedAge === ag
                  ? "bg-[#1E2761] text-white border-[#1E2761]"
                  : "bg-white text-gray-600 border-gray-300 hover:border-[#1E2761] hover:text-[#1E2761]"
              }`}
              data-testid={`age-filter-${ag.toLowerCase()}`}
            >
              {ag}
            </button>
          ))}
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STAT_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <Card
                key={card.label}
                className="border border-gray-200 shadow-sm cursor-pointer hover:shadow-md hover:border-gray-300 transition-all group"
                onClick={() => navigate(card.href)}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${card.bg} flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform`}>
                    <Icon className={`h-5 w-5 ${card.color}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 truncate">{card.label}</p>
                    <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTAs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card className="border border-blue-200 bg-blue-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate(tournamentHref)}
            data-testid="cta-manage-tournament"
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-blue-800 flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Manage Tournament
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-blue-700">
                {selectedAge === "All"
                  ? "View registrations, team approvals, and payment tracking for Spring Tournament."
                  : `View ${selectedAge} registrations, team approvals, and payment tracking.`}
              </p>
              <Button
                variant="ghost"
                className="mt-3 h-8 px-0 text-blue-700 hover:text-blue-900 hover:bg-transparent text-sm font-semibold"
                onClick={(e) => { e.stopPropagation(); navigate(tournamentHref); }}
              >
                Open Tournament <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>

          <Card className="border border-purple-200 bg-purple-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate(selectionHref)}
            data-testid="cta-open-selection"
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-purple-800 flex items-center gap-2">
                <CheckSquare className="h-5 w-5" />
                Open Selection Panel
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-purple-700">
                {selectedAge === "All"
                  ? `Review Spring Trial scores across ${MOCK_CANDIDATES.length} candidates and 3 selectors.`
                  : `Review ${selectedAge} Spring Trial scores across ${filteredCandidates.length} candidate${filteredCandidates.length !== 1 ? "s" : ""} and 3 selectors.`}
              </p>
              <Button
                variant="ghost"
                className="mt-3 h-8 px-0 text-purple-700 hover:text-purple-900 hover:bg-transparent text-sm font-semibold"
                onClick={(e) => { e.stopPropagation(); navigate(selectionHref); }}
              >
                Open Selection <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card className="border border-gray-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-gray-800">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="space-y-3">
              {RECENT_ACTIVITY.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className="text-xs text-gray-400 shrink-0 pt-0.5 w-20">{item.time}</span>
                  <span className="text-gray-700">{item.text}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </HubAdminDemoLayout>
  );
}
